import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { AccessLevels, editRole, roleDetails } from "../action/role";
import { toast } from "react-toastify";
import validator from "validator";

const EditRole = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [accessLevels, setAccessLevels] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [roleName, setRoleName] = useState("");
  const [desc, setDesc] = useState("");
  const [errors, setErrors] = useState({});
  /* ---------------- FETCH ACCESS LEVELS ---------------- */

  const fetchAccessLevels = async () => {
    try {
      const res = await AccessLevels();
      if (res?.status) {
        setAccessLevels(res.data.items);
      }
    } catch (error) {
      console.log("Access Level Error");
    }
  };

  /* ---------------- FETCH ROLE DETAILS ---------------- */

  const fetchRoleDetail = async () => {
    try {
      const res = await roleDetails(id);
      if (res?.status) {
        const role = res.data;

        setRoleName(role.name);
        setDesc(role.description || "");

        initializePermissions(role.user_access_levels);
      }
    } catch (error) {
      console.log("Role Detail Error");
    }
  };

  /* ---------------- INITIALIZE PERMISSIONS ---------------- */

  const initializePermissions = (roleAccessLevels) => {
    const initialState = {};

    accessLevels.forEach((item) => {
      const existing = roleAccessLevels.find(
        (r) => r.access_level_id === item._id
      );

      if (existing) {
        const op = existing.operation || "";

        const create = op.includes("C");
        const view = true; // always true
        const edit = op.includes("U");
        const all = op === "CRUD";

        initialState[item._id] = {
          is_active: existing.is_active,
          create,
          view,
          edit,
          all,
        };
      } else {
        initialState[item._id] = {
          is_active: false,
          create: false,
          view: true,
          edit: false,
          all: false,
        };
      }
    });

    setPermissions(initialState);
  };

  /* ---------------- USE EFFECT ---------------- */

  useEffect(() => {
    fetchAccessLevels();
  }, []);

  useEffect(() => {
    if (accessLevels.length > 0) {
      fetchRoleDetail();
    }
  }, [accessLevels]);

  /* ---------------- HANDLERS ---------------- */

  const handleMainToggle = (id) => {
    setPermissions((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        is_active: !prev[id].is_active,
      },
    }));
  };

  const handleCheckbox = (id, type) => {
    setPermissions((prev) => {
      const current = prev[id];

      if (type === "all") {
        const newValue = !current.all;

        return {
          ...prev,
          [id]: {
            ...current,
            all: newValue,
            create: newValue,
            edit: newValue,
            view: true,
          },
        };
      }

      const updated = {
        ...current,
        [type]: !current[type],
      };

      if (updated.create && updated.edit && updated.view) {
        updated.all = true;
      } else {
        updated.all = false;
      }

      return {
        ...prev,
        [id]: updated,
      };
    });
  };

  const buildOperationString = (perm) => {
    if (!perm.is_active) return "R";

    if (perm.all) return "CRUD";

    let op = "";
    if (perm.create) op += "C";
    if (perm.view) op += "R";
    if (perm.edit) op += "U";

    return op || "R";
  };

  /* ---------------- SUBMIT ---------------- */

  const validateForm = () => {
    let newErrors = {};

    if (!validator.trim(roleName || "")) {
      newErrors.roleName = "Role name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    try {
      if (!validateForm()) return;
      const user_access_levels = accessLevels.map((item) => ({
        access_level_id: item._id,
        operation: buildOperationString(permissions[item._id]),
        is_active: permissions[item._id]?.is_active || false,
      }));

      const payload = {
        name: roleName,
        description: desc,
        user_access_levels,
      };
      //   console.log("UPDATE PAYLOAD:", payload);
      const res = await editRole(id, payload);
      if (res.status) {
        toast.success(res.message);
        navigate("/role");
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error("Falied to update role");
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <>
      <PageHeader pageTitle="Edit Role" is_deploy_enable={false}>
        <button className="btn-white me-3">Discard</button>
        <button className="btn-black" onClick={handleUpdate}>
          Update Role
        </button>
      </PageHeader>

      <div className="page-container">
        <div className="row">
          <div className="col-lg-6">
            <div className="theme-card p-4 mb-4">
              <div className="card-title pb-3">Role Information</div>

              <div className="frm-group">
                <label className="frm-label">Role Name</label>
                <input
                  type="text"
                  className="frm-input"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                />
                {errors.roleName && (
                  <div className="text-danger mt-1">{errors.roleName}</div>
                )}
              </div>

              <div className="frm-group">
                <label className="frm-label">Description</label>
                <textarea
                  rows={3}
                  className="frm-input"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="theme-card p-4 mb-4">
              <div className="card-title pb-3">Access Levels</div>

              {accessLevels.map((l) => {
                const perm = permissions[l._id] || {};

                return (
                  <div
                    key={l._id}
                    className="d-flex justify-content-between py-3 border-bottom permission_card"
                  >
                    <div className="permission_box">
                      <p className="permission_ttl">{l.menu}</p>
                      <p className="permission_sub_ttl">
                        {l.desc ||
                          "Create, edit, assign and track project-level information."}
                      </p>

                      {perm.is_active && (
                        <div className="check_boxes_container">
                          <div className="common_check">
                            <label>
                              All
                              <input
                                type="checkbox"
                                checked={perm.all}
                                onChange={() => handleCheckbox(l._id, "all")}
                              />
                            </label>
                          </div>

                          <div className="common_check">
                            <label>
                              Create
                              <input
                                type="checkbox"
                                checked={perm.create}
                                onChange={() => handleCheckbox(l._id, "create")}
                              />
                            </label>
                          </div>

                          <div className="common_check">
                            <label>
                              Edit
                              <input
                                type="checkbox"
                                checked={perm.edit}
                                onChange={() => handleCheckbox(l._id, "edit")}
                              />
                            </label>
                          </div>

                          <div className="common_check">
                            <label>
                              View
                              <input type="checkbox" checked={true} disabled />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0 pt-1">
                      <label className="custom-switch">
                        <input
                          type="checkbox"
                          checked={perm.is_active}
                          onChange={() => handleMainToggle(l._id)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditRole;
